import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Heart, 
  HeartOff,
  Star,
  MapPin,
  Calendar,
  User,
  Scissors,
  Plus,
  Trash2,
  Check,
  Edit2
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const MyFavorites = ({ token }) => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [defaultStylist, setDefaultStylist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, [token]);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/clients/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data?.stylists || []);
      setDefaultStylist(response.data?.default_stylist_id || null);
    } catch (error) {
      console.error("Failed to fetch favorites:", error);
      toast.error("Failed to load favorites");
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (stylistId) => {
    try {
      await axios.delete(`${API}/clients/favorites/${stylistId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(prev => prev.filter(f => f.user_id !== stylistId));
      if (defaultStylist === stylistId) {
        setDefaultStylist(null);
      }
      toast.success("Removed from favorites");
    } catch (error) {
      toast.error("Failed to remove favorite");
    }
  };

  const setAsDefault = async (stylistId) => {
    try {
      await axios.put(`${API}/clients/favorites/default`, 
        { stylist_id: stylistId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDefaultStylist(stylistId);
      toast.success("Default stylist updated!");
    } catch (error) {
      toast.error("Failed to set default stylist");
    }
  };

  const removeDefault = async () => {
    try {
      await axios.delete(`${API}/clients/favorites/default`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefaultStylist(null);
      toast.success("Default stylist removed");
    } catch (error) {
      toast.error("Failed to remove default");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">My Barbers & Stylists</h2>
          <p className="text-gray-500">Your saved favorites - auto-populate when booking</p>
        </div>
        <Button 
          onClick={() => navigate("/find-stylists")}
          className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Find More
        </Button>
      </div>

      {/* Default Stylist Card */}
      {defaultStylist && favorites.find(f => f.user_id === defaultStylist) && (
        <Card className="border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-orange-700">
              <Star className="w-5 h-5 fill-orange-500" />
              My Default Stylist
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const stylist = favorites.find(f => f.user_id === defaultStylist);
              return (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                      <User className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 text-lg">{stylist?.name}</h3>
                      <p className="text-orange-600 capitalize">{stylist?.professional_type || "Stylist"}</p>
                      {stylist?.city && (
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {stylist.city}, {stylist.state}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={removeDefault}
                      className="text-gray-500"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Change
                    </Button>
                    <Button 
                      className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                      onClick={() => navigate(`/book?stylist=${defaultStylist}`)}
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Book Now
                    </Button>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Favorites List */}
      {favorites.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">No Favorites Yet</h3>
            <p className="text-gray-400 text-sm mb-6">
              Save your favorite barbers and stylists for quick booking
            </p>
            <Button 
              onClick={() => navigate("/find-stylists")}
              className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Find Stylists
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <h3 className="font-medium text-gray-700">All Favorites ({favorites.length})</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {favorites.map((stylist) => (
              <Card 
                key={stylist.user_id} 
                className={`hover:shadow-md transition-all ${
                  defaultStylist === stylist.user_id 
                    ? "ring-2 ring-orange-300 bg-orange-50/30" 
                    : ""
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center">
                        <User className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-800">{stylist.name}</h3>
                          {defaultStylist === stylist.user_id && (
                            <Badge className="bg-orange-100 text-orange-700 text-xs">
                              <Star className="w-3 h-3 mr-1 fill-current" />
                              Default
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-orange-600 capitalize">
                          {stylist.professional_type || "Stylist"}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                          {stylist.rating > 0 && (
                            <span className="flex items-center gap-1">
                              <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                              {stylist.rating?.toFixed(1)}
                            </span>
                          )}
                          {stylist.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {stylist.city}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => removeFavorite(stylist.user_id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove from favorites"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex gap-2 mt-4">
                    {defaultStylist !== stylist.user_id && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => setAsDefault(stylist.user_id)}
                        className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                      >
                        <Star className="w-4 h-4 mr-1" />
                        Set as Default
                      </Button>
                    )}
                    <Button 
                      size="sm"
                      className={`${defaultStylist !== stylist.user_id ? "flex-1" : "w-full"} bg-gradient-to-r from-orange-500 to-amber-500 text-white`}
                      onClick={() => navigate(`/book?stylist=${stylist.user_id}`)}
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Book
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Heart className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">About Favorites</p>
              <ul className="list-disc list-inside space-y-1 text-blue-600">
                <li>Your default stylist is auto-selected when booking</li>
                <li>You can have multiple favorites for different services</li>
                <li>Change your default anytime from this page</li>
                <li>Find new stylists using the search feature</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyFavorites;
